import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';

const DESKTOP_MEDIA = '(min-width: 768px)';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_MEDIA).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MEDIA);
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

/**
 * HTML/текст с ограничением по числу строк; кнопка «Показать еще» только если контент длиннее.
 * collapseOnMobileOnly: на md+ полный текст без кнопки; сворачивание только на узких экранах.
 */
const ExpandableProse = ({
  html,
  lines = 5,
  className = 'prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap',
  collapseOnMobileOnly = false,
}) => {
  const contentRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const isDesktop = useIsDesktop();
  const canCollapse = !collapseOnMobileOnly || !isDesktop;

  useEffect(() => {
    setExpanded(false);
  }, [html]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || !html || !canCollapse) {
      setShowToggle(false);
      return undefined;
    }

    const measure = () => {
      if (expanded) {
        setShowToggle(true);
        return;
      }
      setShowToggle(el.scrollHeight > el.clientHeight + 2);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [html, expanded, lines, canCollapse]);

  if (!html) {
    return null;
  }

  const clampStyle =
    canCollapse && !expanded
      ? {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: lines,
          overflow: 'hidden',
        }
      : undefined;

  return (
    <div>
      <div
        ref={contentRef}
        className={className}
        style={clampStyle}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {canCollapse && showToggle && (
        <div className="mt-4">
          <Button type="button" variant="outline" onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? 'Скрыть' : 'Показать еще'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpandableProse;
